import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import Reveal from '@/components/feature/Reveal';

export default function Contact() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('company_alt') as string)?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    // Remove honeypot from payload
    formData.delete('company_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/da4b1m0et96csusepmm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const message = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (message?.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError(t('contact.form.spamError'));
      } else {
        setFormStatus('error');
        setFormError(message || t('contact.form.error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(t('contact.form.error'));
    }
  };

  return (
    <Layout showTopBar>
      {/* Hero */}
      <section className="relative w-full h-[360px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20cafe%20counter%20with%20barista%20serving%20coffee%2C%20warm%20lighting%2C%20clean%20minimalist%20design%2C%20wood%20and%20white%20interior%2C%20friendly%20atmosphere%2C%20editorial%20photography%2C%20soft%20natural%20light%2C%20professional%20hospitality%20scene&width=1600&height=700&seq=contact-hero-01&orientation=landscape"
          alt="BIOLA Cafe Contact"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-foreground-950/50" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center max-w-2xl">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3 animate-hero-content">
              {t('contact.pageTitle')}
            </h1>
            <p className="text-background-100 text-base md:text-lg animate-hero-content-delay">
              {t('contact.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10 md:mb-14 items-stretch">
            {[
              { icon: 'ri-phone-line', title: t('contact.phone'), value: '050 603 2163', href: 'tel:+971506032163' },
              { icon: 'ri-mail-line', title: t('contact.email'), value: 'biola.ae1@gmail.com', href: 'mailto:biola.ae1@gmail.com' },
              { icon: 'ri-map-pin-line', title: t('contact.address'), value: 'ADNOC Service Station | Mafraq (171 - Bani Yas - Baniyas East - Abu Dhabi', href: null },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 70} className="h-full">
                <div
                  className="group bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 text-center hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col justify-center"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mx-auto mb-3 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <h3 className="font-heading text-sm font-semibold text-foreground-700 mb-1">{item.title}</h3>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-foreground-950 text-sm font-medium hover:text-primary-600 transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-foreground-950 text-sm font-medium">{item.value}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          {/* Contact Form + Map */}
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {/* Form */}
              <div className="bg-background-100 rounded-xl p-5 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                  {t('contact.formTitle')}
                </h2>
                <p className="text-foreground-500 text-sm mb-6">
                  {t('contact.formDesc')}
                </p>

                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-accent-100 border border-accent-200/70 rounded-xl">
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-accent-600" />
                      <p className="text-accent-800 text-sm font-medium">{t('contact.form.success')}</p>
                    </div>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-accent-100 border border-accent-200/70 rounded-xl">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line text-accent-600" />
                      <p className="text-accent-800 text-sm">{formError}</p>
                    </div>
                  </div>
                )}

                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.form.name')}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                        placeholder={t('contact.form.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.form.email')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.subject')}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                      placeholder={t('contact.form.subjectPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none"
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                    <p className="text-foreground-400 text-xs mt-1">{t('contact.form.maxChars')}</p>
                  </div>

                  {/* Honeypot */}
                  <div className="company-alt-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm"
                      placeholder="Company Name"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'success'}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'success' ? t('contact.form.sent') : t('contact.form.send')}
                  </button>
                </form>
              </div>

              {/* Map */}
              <div className="bg-background-100 rounded-xl p-5 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                  {t('contact.visitTitle')}
                </h2>
                <p className="text-foreground-500 text-sm mb-6">
                  {t('contact.visitDesc')}
                </p>
                <div className="rounded-xl overflow-hidden border border-background-200/70 h-[300px] md:h-[380px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d232137.67439860592!2d54.30219!3d24.4539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e440f723ef2b9%3A0x5469fb0c088b6b1f!2sAbu%20Dhabi%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2sus!4v1700000000000"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="BIOLA Abu Dhabi Location"
                    className="w-full h-full"
                  />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className="ri-time-line" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{t('contact.hours')}</p>
                    <p className="text-xs text-foreground-500">{t('contact.hoursValue')}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}